import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';


import notificationService from '../../services/notificationService';
import conferenceService from '../../services/conferenceService';
import NotificationForm from './components/NotificationForm';
import NotificationHistory from './components/NotificationHistory';
import TemplateManager from './components/TemplateManager';
import StatsCards from './components/StatsCards';

const NotificationManagementScreen = () => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Accepts both spellings. The app used ?id= on six routes and
  // ?conferenceId= on three, so links between them only worked if you knew
  // which one each page wanted. Everything now *emits* ?conferenceId=;
  // reading ?id= as well keeps already-shared and bookmarked links alive.
  const conferenceId = searchParams?.get('conferenceId') || searchParams?.get('id');
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [conference, setConference] = useState(null);
  const [activeTab, setActiveTab] = useState('send');
  const [templates, setTemplates] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
    } else if (conferenceId) {
      loadData();
    }
  }, [user, conferenceId, navigate, refreshTrigger]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [confData, templatesData, notificationsData, statsData] = await Promise.all([
        conferenceService?.getById(conferenceId),
        notificationService?.getTemplates(conferenceId),
        notificationService?.getNotifications(conferenceId),
        notificationService?.getNotificationStats(conferenceId)
      ]);

      setConference(confData);
      setTemplates(templatesData);
      setNotifications(notificationsData);
      setStats(statsData);
    } catch (err) {
      setError(err?.message || t('notificationManagement.errors.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{t('notificationManagement.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="page-shell py-8">
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('notificationManagement.title')}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {conference?.name || 'Conference'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
              >
                <Icon name="RefreshCw" size={16} className="mr-2" />
                {t('notificationManagement.refresh')}
              </Button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Stats Cards */}
          <StatsCards stats={stats} />

          {/* Tabs */}
          <div className="mt-6">
            <div className="border-b border-border">
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('send')}
                  className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'send' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Send" size={16} className="inline mr-2" />
                  {t('notificationManagement.tabs.sendNotification')}
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'history' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="History" size={16} className="inline mr-2" />
                  {t('notificationManagement.tabs.history')}
                </button>
                <button
                  onClick={() => setActiveTab('templates')}
                  className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'templates' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="FileText" size={16} className="inline mr-2" />
                  {t('notificationManagement.tabs.templates')}
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === 'send' && (
                <NotificationForm
                  conferenceId={conferenceId}
                  templates={templates}
                  onSuccess={handleRefresh}
                />
              )}
              {activeTab === 'history' && (
                <NotificationHistory
                  notifications={notifications}
                  onRefresh={handleRefresh}
                />
              )}
              {activeTab === 'templates' && (
                <TemplateManager
                  conferenceId={conferenceId}
                  templates={templates}
                  onUpdate={handleRefresh}
                />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationManagementScreen;